import React, { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { PlaylistDataContext } from '../DataContexts/PlaylistDataContext';
import { placePopover } from './popoverPlacement';

// DJB-266: the popover above a green match panel — search the playlists of
// GET /playlists (a USER sees all of them; there is no scoping per owner
// yet), collect the chosen ones as chips, and "Add to playlists" sends the
// matched library track to each of them through POST /playlists/{id}/tracks
// (DJB-272). That endpoint appends: the tracks already in a playlist stay,
// and a track that was already there comes back as `skipped` instead of
// being duplicated. Every playlist gets its own success / "already in" /
// error line. The request is NOT completed automatically afterwards.
//
// DJB-280: the popover is rendered in a portal on <body> with
// position: fixed (placePopover decides above / below the panel), so it
// lies over the sticky header, the side rail and the mobile menu, is never
// clipped by a card near the top, and follows the panel while scrolling.
// `anchorRef` is the element it belongs to; clicks on it count as inside.
const MAX_SUGGESTIONS = 8;

// Generic (auto-generated) playlists cannot take tracks in the Playlist
// Editor either
export const canTakeTracks = (playlist) => playlist && playlist.playlistType !== 'GENERIC_GLOBAL';

// One result line per playlist, from the answer of addTracksToPlaylist
export const resultFor = (playlist, trackId, result) => {
  if (!result) return { id: playlist.id, type: 'error', text: `Could not add to ${playlist.name}.` };
  if (result.added.includes(trackId)) return { id: playlist.id, type: 'success', text: `Added to ${playlist.name}.` };
  if (result.skipped.includes(trackId)) {
    return { id: playlist.id, type: 'info', text: `Already in ${playlist.name}.` };
  }
  return { id: playlist.id, type: 'error', text: `Could not add to ${playlist.name}.` };
};

const AddToPlaylistPopup = ({ trackId, onClose, anchorRef }) => {
  const { playlists = [], addTracksToPlaylist } = useContext(PlaylistDataContext);
  const [query, setQuery] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState([]);
  const inputRef = useRef(null);
  const rootRef = useRef(null);
  const [position, setPosition] = useState(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Escape and clicking outside close the popover without changing anything;
  // the anchor (the panel with the "Add to Playlist" toggle) counts as inside
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    const onDown = (e) => {
      const inside = (ref) => ref && ref.current && ref.current.contains(e.target);
      if (!inside(rootRef) && !inside(anchorRef)) onClose();
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [onClose, anchorRef]);

  // Place the popover at its anchor after every render (it grows with chips,
  // suggestions and result lines), and again when the page scrolls or the
  // viewport changes
  const reposition = useCallback(() => {
    const anchor = anchorRef && anchorRef.current;
    const el = rootRef.current;
    if (!anchor || !el) return;
    const next = placePopover(
      anchor.getBoundingClientRect(),
      { width: el.offsetWidth, height: el.offsetHeight },
      { width: window.innerWidth, height: window.innerHeight }
    );
    setPosition((prev) =>
      prev &&
      prev.placement === next.placement &&
      prev.top === next.top &&
      prev.left === next.left &&
      prev.tailX === next.tailX
        ? prev
        : next
    );
  }, [anchorRef]);

  useLayoutEffect(reposition);

  useEffect(() => {
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [reposition]);

  const candidates = useMemo(() => playlists.filter(canTakeTracks), [playlists]);
  const selected = selectedIds.map((id) => candidates.find((p) => p.id === id)).filter(Boolean);
  const q = query.trim().toLowerCase();
  const suggestions = candidates
    .filter((p) => !selectedIds.includes(p.id) && (p.name || '').toLowerCase().includes(q))
    .slice(0, MAX_SUGGESTIONS);

  const pick = (playlist) => {
    setSelectedIds((ids) => (ids.includes(playlist.id) ? ids : [...ids, playlist.id]));
    setQuery('');
    setResults([]);
    inputRef.current?.focus();
  };

  const remove = (id) => {
    setSelectedIds((ids) => ids.filter((x) => x !== id));
    setResults([]);
  };

  const onInput = (e) => {
    setQuery(e.target.value);
    setResults([]);
  };

  // Enter picks the only / first suggestion, Backspace in an empty field
  // drops the last chip
  const onKeyDown = (e) => {
    if (e.key === 'Enter' && suggestions.length > 0) {
      e.preventDefault();
      pick(suggestions[0]);
    } else if (e.key === 'Backspace' && !query && selectedIds.length > 0) {
      remove(selectedIds[selectedIds.length - 1]);
    }
  };

  const add = async () => {
    if (selected.length === 0 || busy) return;
    setBusy(true);
    try {
      const lines = [];
      for (const playlist of selected) {
        let result = null;
        try {
          result = await addTracksToPlaylist(playlist.id, [trackId]);
        } catch (e) {
          console.error('Failed to add track to playlist:', e);
        }
        lines.push(resultFor(playlist, trackId, result));
      }
      setResults(lines);
      // The playlists that took the track (or already had it) leave the
      // selection; the failed ones stay so they can be retried
      const failed = lines.filter((l) => l.type === 'error').map((l) => l.id);
      setSelectedIds((ids) => ids.filter((id) => failed.includes(id)));
    } finally {
      setBusy(false);
    }
  };

  const hasError = results.some((r) => r.type === 'error');

  const placement = position ? position.placement : 'above';
  const style = position
    ? { top: position.top, left: position.left, '--tail-x': `${position.tailX}px` }
    : { top: 0, left: 0, visibility: 'hidden' };

  const popup = (
    <div
      className={`add-to-playlist is-${placement}`}
      role="dialog"
      aria-label="Add to playlists"
      ref={rootRef}
      style={style}
      data-placement={placement}
    >
      <div className="add-to-playlist-field">
        <input
          ref={inputRef}
          type="text"
          className="add-to-playlist-input"
          placeholder="search playlist"
          aria-label="Search playlist"
          aria-autocomplete="list"
          value={query}
          onChange={onInput}
          onKeyDown={onKeyDown}
          disabled={busy}
        />
        {q && suggestions.length > 0 && (
          <ul className="add-to-playlist-suggestions" role="listbox">
            {suggestions.map((p) => (
              <li key={p.id} role="option" aria-selected={false}>
                <button type="button" className="add-to-playlist-suggestion" onClick={() => pick(p)}>
                  {p.name}
                </button>
              </li>
            ))}
          </ul>
        )}
        {q && suggestions.length === 0 && <div className="add-to-playlist-empty">No playlist found.</div>}
      </div>
      {selected.length > 0 && (
        <ul className="add-to-playlist-chips" aria-label="Chosen playlists">
          {selected.map((p) => (
            <li key={p.id} className="add-to-playlist-chip">
              <span className="add-to-playlist-chip-name">{p.name}</span>
              <button
                type="button"
                className="add-to-playlist-chip-remove"
                aria-label={`Remove ${p.name}`}
                disabled={busy}
                onClick={() => remove(p.id)}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}
      {results.length > 0 && (
        <ul className="add-to-playlist-results" role={hasError ? 'alert' : 'status'}>
          {results.map((r) => (
            <li key={r.id} className={`add-to-playlist-result is-${r.type}`}>
              {r.text}
            </li>
          ))}
        </ul>
      )}
      <div className="add-to-playlist-actions">
        <button type="button" className="add-to-playlist-cancel" onClick={onClose}>
          {results.length > 0 && selected.length === 0 ? 'Close' : 'Cancel'}
        </button>
        <button
          type="button"
          className="add-to-playlist-confirm"
          disabled={selected.length === 0 || busy}
          onClick={add}
        >
          {busy ? 'Adding…' : 'Add to playlists'}
        </button>
      </div>
    </div>
  );

  return createPortal(<div className="add-to-playlist-layer">{popup}</div>, document.body);
};

export default AddToPlaylistPopup;
